import { useCallback, useEffect, useState } from "react";

import { NOTIFICATION_TYPE_ALL } from "~/constants/dropdowns/notifications/typeOptions";
import { listNotifications } from "~/graphql/queries";
import { onNotificationCreates } from "~/graphql/subscriptions";
import appsyncClient from "~/lib/appsyncClient";
import { withRetry } from "~/lib/graphqlRetry";
import { createManagedSubscription } from "~/lib/subscriptionManager";

const useNotifications = (UserID, TeamID, Type, showOnlyUnread) => {
  const [notifications, setNotifications] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await withRetry(() =>
        appsyncClient.graphql({
          query: listNotifications,
          variables: {
            UserID,
            TeamID,
            Type,
            showOnlyUnread,
            nextToken: null,
          },
        }),
      );

      const { items, nextToken: newToken } = result.data?.listNotifications;

      // Backend returns items sorted by CreatedAt descending via GSI
      setNotifications(items ?? []);
      setNextToken(newToken);
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
      setError("Failed to fetch notifications. Please try again.");
      setNotifications([]);
      setNextToken(null);
    } finally {
      setIsLoading(false);
    }
  }, [UserID, TeamID, Type, showOnlyUnread]);

  const fetchNextPageOfNotifications = useCallback(async () => {
    setIsNextPageLoading(true);
    setError(null);

    try {
      const result = await withRetry(() =>
        appsyncClient.graphql({
          query: listNotifications,
          variables: {
            UserID,
            TeamID,
            Type,
            showOnlyUnread,
            nextToken,
          },
        }),
      );

      const { items, nextToken: newToken } = result.data?.listNotifications;

      // Append new page - already sorted by CreatedAt descending from backend
      setNotifications((prev) => [...prev, ...(items ?? [])]);
      setNextToken(newToken);
    } catch (e) {
      console.error("Failed to fetch next page of notifications:", e);
      setError("Failed to fetch notifications. Please try again.");
    } finally {
      setIsNextPageLoading(false);
    }
  }, [UserID, TeamID, Type, showOnlyUnread, nextToken]);

  useEffect(() => {
    setNextToken(null);
    fetchNotifications();
  }, [Type, showOnlyUnread, fetchNotifications]);

  useEffect(() => {
    const handleData = (data) => {
      const newNotification = data.onNotificationCreates;
      if (!newNotification) {
        return;
      }

      // Check if notification matches current filters
      const matchesReadFilter = !showOnlyUnread || !newNotification.IsRead;
      const matchesTypeFilter =
        !Type ||
        Type === NOTIFICATION_TYPE_ALL ||
        newNotification.Type === Type;

      if (matchesReadFilter && matchesTypeFilter) {
        setNotifications((prevNotifications) => [
          newNotification,
          ...prevNotifications,
        ]);
      }
    };

    const handleError = (subscriptionError) => {
      console.error("Notifications subscription error:", subscriptionError);
    };

    const handleReconnecting = () => {
      setIsReconnecting(true);
    };

    const handleReconnected = () => {
      setIsReconnecting(false);
    };

    const handleMaxRetriesReached = () => {
      setIsReconnecting(false);
      console.error(
        "Notifications subscription: max reconnection attempts reached",
      );
    };

    const controller = createManagedSubscription({
      query: onNotificationCreates,
      variables: {
        UserID,
        TeamID,
      },
      onData: handleData,
      onError: handleError,
      onReconnecting: handleReconnecting,
      onReconnected: handleReconnected,
      onMaxRetriesReached: handleMaxRetriesReached,
    });

    return () => controller.unsubscribe();
  }, [UserID, TeamID, showOnlyUnread, Type]);

  return {
    notifications,
    isLoading,
    isNextPageLoading,
    error,
    nextToken,
    isReconnecting,
    setNotifications,
    fetchNotifications,
    fetchNextPageOfNotifications,
  };
};

export default useNotifications;
